<template>
  <div class="p-room">
    <div class="p-room__content">
      <Guide v-if="!roomId">
        <i class="fas fa-comment-dots fa-4x c-text--primary"></i>
        <p>チャットルームを選択してください。</p>
      </Guide>
      <Loading v-else-if="!isRoomSynced" variant="main" />
      <Guide v-else-if="!isCreated">
        <i class="fas fa-comment-slash fa-4x c-text--secondary"></i>
        <p>チャットルームが見つかりません。</p>
      </Guide>
      <Loading v-else-if="isLoading" variant="main" />
      <Guide v-else-if="!hasMessage">
        <i class="fas fa-comment-dots fa-4x c-text--primary"></i>
        <p>チャットを始めましょう！</p>
      </Guide>
      <Timeline
        v-else
        :firstUnreadMessageId="firstUnreadMessageId"
        :messages="sortedMessages"
        :roomLastDatetime="roomLastDatetime"
        :isLoadingMore="isLoadingMore"
        @loadMore="setMoreMessageListener"
      />
    </div>
    <div v-if="room && !isLoading && !isReadOnlyMode" class="p-room__footer">
      <Form />
    </div>
  </div>
</template>

<script>
import { MESSAGES_GET_LIMIT } from '@/constants';
import Form from '@/components/room/Form';
import Guide from '@/components/room/Guide';
import Loading from '@/components/Loading';
import Timeline from '@/components/room/Timeline';
import utils from '@/utils';
import {
  EXTERNAL_SERVICE,
  EXTERNAL_SERVICE_ACTION
} from '@/analytics/constants';
import {
  captureAndRethrowExternalServiceError,
  reportExternalServiceError
} from '@/analytics/reportExternalServiceError';

export default {
  components: {
    Form,
    Guide,
    Loading,
    Timeline
  },
  data() {
    return {
      firstUnreadMessageId: null,
      listeners: [],
      messages: [],
      persistedFirstUnreadMessageId: null,
      roomLastDatetime: null,
      sessionManualUnreadDatetime: null,
      sessionManualUnreadMessageId: null,
      startAt: null,
      endBefore: null,
      isLoading: true,
      isLoadingMore: false
    };
  },
  computed: {
    hasMessage() {
      return this.sortedMessages.length;
    },
    lastMessage() {
      return this.sortedMessages[this.sortedMessages.length - 1];
    },
    role() {
      return this.$store.state.role;
    },
    room() {
      // room.idはプロジェクト一覧取得APIのデータから作られる
      // roomIdとroom.idが合致しなければ、nullが返ってくる
      return this.$store.getters.currentRoom;
    },
    roomId() {
      // roomIdはクエリパラメータから作られる
      return this.$store.getters.currentRoomId;
    },
    sortedMessages() {
      return this.messages.slice().sort((a, b) => {
        if (!a.datetime && !b.datetime) {
          return 0;
        } else if (!a.datetime) {
          return 1;
        } else if (!b.datetime) {
          return -1;
        }
        return utils.compareTimestamp(a.datetime, b.datetime);
      });
    },
    isCompany() {
      return utils.isCompany(this.role.name);
    },
    isCreated() {
      return utils.isCreatedRoom(this.room);
    },
    isRoomSynced() {
      return this.$store.getters.isCurrentRoomSynced;
    },
    isReadOnlyMode() {
      return this.$store.state.isReadOnlyMode;
    }
  },
  watch: {
    'lastMessage.datetime': {
      handler(value) {
        if (value) {
          this.updateLastDatetime(value);
        }
      }
    },
    roomId(value, oldValue) {
      if (value === oldValue) {
        return;
      }
      this.resetMessageListener();
      this.firstUnreadMessageId = null;
      this.persistedFirstUnreadMessageId = null;
      this.messages = [];
      this.startAt = null;
      this.endBefore = null;
      this.isLoading = !!value && this.isCreated;
      this.isLoadingMore = false;
      if (value && this.isCreated) {
        this.captureSessionUnreadState();
        this.roomLastDatetime = this.room.lastDatetime;
        this.setMessageListener();
      }
    },
    isCreated: {
      handler(value) {
        if (value) {
          // ルームのデータは表示後に届くことがあるため、メッセージ取得まではローディングにする
          this.isLoading = true;
          // ルーム読み込み時点の最終閲覧日時を残しておく
          // この日時を基準にしてメッセージの未読判定を行う
          this.captureSessionUnreadState();
          this.roomLastDatetime = this.room.lastDatetime;
          this.setMessageListener();
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.$root.$on('room-manual-unread-set', this.handleManualUnreadSet);
  },
  beforeDestroy() {
    this.$root.$off('room-manual-unread-set', this.handleManualUnreadSet);
    this.resetMessageListener();
  },
  methods: {
    captureSessionUnreadState() {
      this.sessionManualUnreadDatetime =
        this.isCompany && this.room
          ? this.room.companyManualUnreadDatetime
          : null;
      this.sessionManualUnreadMessageId =
        this.isCompany && this.room
          ? this.room.companyManualUnreadMessageId
          : null;
    },
    reportMessageReadError: captureAndRethrowExternalServiceError(
      EXTERNAL_SERVICE.FIRESTORE,
      EXTERNAL_SERVICE_ACTION.CHAT_MESSAGES_READ
    ),
    reportMessageListenerError(error) {
      reportExternalServiceError({
        service: EXTERNAL_SERVICE.FIRESTORE,
        action_type: EXTERNAL_SERVICE_ACTION.CHAT_MESSAGES_LISTEN,
        error
      });
    },
    getFirstUnreadMessage() {
      const ref = this.$firebase.db.collection(
        `rooms/${this.room.id}/messages`
      );
      if (!this.isCompany) {
        return ref
          .where('datetime', '>', this.roomLastDatetime)
          .orderBy('datetime', 'asc')
          .limit(1)
          .get()
          .catch(this.reportMessageReadError)
          .then(snapshot => {
            if (!snapshot || snapshot.empty) {
              return null;
            }
            return snapshot.docs[0].id;
          });
      }
      return Promise.resolve(this.sessionManualUnreadMessageId || null);
    },
    syncFirstUnreadMessageId() {
      if (!this.isCompany || !this.room) {
        return Promise.resolve();
      }
      return this.getFirstUnreadMessage(false).then(messageId => {
        if (messageId) {
          this.firstUnreadMessageId = messageId;
          this.persistedFirstUnreadMessageId = messageId;
          return;
        }
        this.firstUnreadMessageId = this.persistedFirstUnreadMessageId;
      });
    },
    handleManualUnreadSet(payload) {
      if (!payload || !this.room || payload.roomId !== this.room.id) {
        return;
      }
      this.sessionManualUnreadDatetime = payload.datetime;
      this.sessionManualUnreadMessageId = payload.messageId;
      this.syncFirstUnreadMessageId();
    },
    setMessageListener() {
      const ref = this.$firebase.db.collection(
        `rooms/${this.room.id}/messages`
      );
      ref
        .orderBy('datetime', 'desc')
        .limit(MESSAGES_GET_LIMIT)
        .get()
        .catch(this.reportMessageReadError)
        .then(snapshot => {
          this.applyMessageChanges(snapshot);
          this.startAt = snapshot.docs[snapshot.docs.length - 1];
          this.isLoading = false;
          this.isLoadingMore = false;

          let listener;
          if (this.startAt) {
            listener = ref
              .orderBy('datetime')
              .startAt(this.startAt)
              .onSnapshot(this.handleMessages, this.reportMessageListenerError);
          } else {
            listener = ref
              .orderBy('datetime')
              .onSnapshot(this.handleMessages, this.reportMessageListenerError);
          }
          this.listeners.push(listener);

          return this.getFirstUnreadMessage();
        })
        .then(messageId => {
          this.firstUnreadMessageId = messageId;
          this.persistedFirstUnreadMessageId = messageId;
        });
    },
    setMoreMessageListener() {
      if (!this.startAt) {
        return;
      }
      this.isLoadingMore = true;

      const ref = this.$firebase.db.collection(
        `rooms/${this.room.id}/messages`
      );
      ref
        .orderBy('datetime', 'desc')
        .startAt(this.startAt)
        .limit(MESSAGES_GET_LIMIT)
        .get()
        .catch(this.reportMessageReadError)
        .then(snapshot => {
          this.endBefore = this.startAt;
          this.startAt = snapshot.docs[snapshot.docs.length - 1];
          let listener = ref
            .orderBy('datetime')
            .startAt(this.startAt)
            .endBefore(this.endBefore)
            .onSnapshot(this.handleMessages, this.reportMessageListenerError);
          this.listeners.push(listener);
        });
    },
    resetMessageListener() {
      this.listeners.forEach(listener => listener());
      this.listeners = [];
    },
    applyMessageChanges(snapshot) {
      snapshot.docChanges().forEach(change => {
        if (change.type === 'added') {
          this.addMessage(change.doc);
        } else if (change.type === 'modified') {
          this.updateMessage(change.doc);
        } else if (change.type === 'removed') {
          this.removeMessage(change.doc);
        }
      });
    },
    handleMessages(snapshot) {
      this.applyMessageChanges(snapshot);
      this.syncFirstUnreadMessageId().finally(() => {
        this.isLoading = false;
        this.isLoadingMore = false;
      });
    },
    addMessage(doc) {
      for (let i = 0; i < this.messages.length; i++) {
        if (this.messages[i].id === doc.id) {
          this.updateMessage(doc);
          return;
        }
      }
      let message = doc.data();
      message.id = doc.id;
      this.messages.push(message);
    },
    updateMessage(doc) {
      let message = doc.data();
      let messages = this.messages;
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].id === doc.id) {
          // updateされる可能性があるものだけupdateする
          messages[i].datetime = message.datetime;
          messages[i].imageUrl = message.imageUrl;
          messages[i].storageUri = message.storageUri;
          messages[i].fileUrl = message.fileUrl;
          messages[i].message = message.message;
          messages[i].isEdited = message.isEdited || false;
          messages[i].isDeleted = message.isDeleted || false;
          break;
        }
      }
    },
    removeMessage(doc) {
      let messages = this.messages;
      for (let i = 0; i < messages.length; i++) {
        if (messages[i].id === doc.id) {
          messages.splice(i, 1);
          break;
        }
      }
    },
    updateLastDatetime(datetime) {
      if (this.isReadOnlyMode) {
        return;
      }
      const data = {};
      data[this.role.name + '_last_datetime'] = datetime;
      data[this.role.name + '_total_unread'] = 0;
      this.$firebase.updateRoomData(this.room.id, data);
    }
  }
};
</script>
